import { c as _c } from "react/compiler-runtime";
import { usePickerContext } from "@mui/x-date-pickers-pro";
import { CalendarIcon, ChevronUpIcon, TextField } from "@flat-ui";
import { formatRangeLabel } from "@components/date-range-picker/date-range-picker-utils";

/** Text shown while no range is selected. */
const PLACEHOLDER = "Jan 1 - Jan 31";
/**
 * The picker's field: a read-only summary of the selected range, e.g. "Jul 16 - Aug 14".
 *
 * From MUI X v9 the built-in `SingleInputDateRangeField` renders an editable `PickersSectionList`
 * rather than a plain input, so setting `slotProps.htmlInput.value` no longer changes what is
 * displayed -- it only sets the value of the hidden input behind the sections. Showing a summary
 * string therefore means replacing the field itself.
 *
 * The picker root styles the field through `.MuiInputBase-*`, which still reaches it: flat-ui's
 * TextField composes an `OutlinedInput` directly, and that is a `MuiInputBase-root`.
 *
 * @returns the field displayed by the date range picker.
 */
export function DateRangeSummaryField(t0) {
  const $ = _c(24);
  const {
    variantStyle
  } = t0;
  const {
    value,
    setOpen,
    triggerRef,
    open: isOpen,
    disabled: isDisabled,
    readOnly: isReadOnly
  } = usePickerContext();
  let t1;
  if ($[0] !== value) {
    t1 = formatRangeLabel(value) ?? "";
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CalendarIcon sx={{
      fontSize: "1.125rem",
      mr: 1
    }} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isOpen ? "none" : "rotate(180deg)";
  let t4;
  if ($[3] !== t3) {
    t4 = <ChevronUpIcon sx={{
      fontSize: "1.125rem",
      transition: "transform 0.2s",
      transform: t3
    }} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      readOnly: true
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isDisabled || $[7] !== isReadOnly || $[8] !== setOpen) {
    t6 = {
      onClick: () => {
        if (!isDisabled && !isReadOnly) {
          setOpen(true);
        }
      }
    };
    $[6] = isDisabled;
    $[7] = isReadOnly;
    $[8] = setOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== variantStyle.inputFieldHoverColor) {
    t7 = {
      color: variantStyle.inputFieldHoverColor,
      display: "block",
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      px: 0
    };
    $[10] = variantStyle.inputFieldHoverColor;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== variantStyle.secondaryColor) {
    t8 = {
      color: variantStyle.secondaryColor,
      opacity: 0.6
    };
    $[12] = variantStyle.secondaryColor;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = {
      "& .MuiInputBase-input": t7,
      "& .MuiInputBase-input::placeholder": t8
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isDisabled || $[18] !== t1 || $[19] !== t4 || $[20] !== t6 || $[21] !== t9 || $[22] !== triggerRef) {
    t10 = <TextField ref={triggerRef} text={t1} placeholder={PLACEHOLDER} disabled={isDisabled} startAdornment={t2} endAdornment={t4} inputProps={t5} InputProps={t6} sx={t9} />;
    $[17] = isDisabled;
    $[18] = t1;
    $[19] = t4;
    $[20] = t6;
    $[21] = t9;
    $[22] = triggerRef;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}